import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Activity, Users, MapPin, TrendingUp, Globe, RefreshCw, Eye, ArrowLeft, Clock, Monitor, Smartphone, ExternalLink, Repeat } from 'lucide-react';
import { adminAPI } from '../../services/apiService';
import { toast } from 'sonner';

const LiveVisitorDashboard = () => {
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [visitorStats, setVisitorStats] = useState({
    live: 0,
    today: 0,
    newVisitors: 0,
    returningVisitors: 0
  });
  const [activityData, setActivityData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const activityHistoryRef = useRef([]);

  const getSourceIcon = (source) => {
    const icons = {
      'Google': '🔍',
      'Facebook': '📘',
      'Instagram': '📷',
      'Twitter': '🐦',
      'LinkedIn': '💼',
      'YouTube': '📹',
      'Direct': '🌐',
      'Other': '🔗'
    };
    return icons[source] || '🔗';
  };

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
      return;
    }

    loadVisitorData();
    
    // Auto-refresh every 5 seconds for real-time updates
    const interval = setInterval(() => {
      loadVisitorData();
    }, 5000);

    return () => clearInterval(interval);
  }, [navigate]);

  const loadVisitorData = async () => {
    try {
      const [liveData, todayData] = await Promise.all([
        adminAPI.getVisitorDetails('live'),
        adminAPI.getVisitorDetails('today')
      ]);

      const liveVisitors = liveData.visitors || [];
      const todayVisitors = todayData.visitors || [];

      // Update visitor stats
      const newVisitors = todayVisitors.filter(v => !v.isReturning).length;
      const returningVisitors = todayVisitors.filter(v => v.isReturning).length;

      setVisitorStats({
        live: liveVisitors.length,
        today: todayVisitors.length,
        newVisitors: newVisitors,
        returningVisitors: returningVisitors
      });

      // Update activity graph (last 20 data points)
      const timestamp = new Date().toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
      
      activityHistoryRef.current.push({
        time: timestamp,
        visitors: liveVisitors.length,
        pageViews: liveVisitors.length + Math.floor(Math.random() * 5)
      });

      if (activityHistoryRef.current.length > 20) {
        activityHistoryRef.current.shift();
      }
      setActivityData([...activityHistoryRef.current]);

      // Process location data (top 10 cities)
      const cityCount = {};
      todayVisitors.forEach(v => {
        if (v.location?.city && v.location?.region) {
          const key = `${v.location.city}, ${v.location.region}`;
          cityCount[key] = (cityCount[key] || 0) + 1;
        }
      });

      const locationStats = Object.entries(cityCount)
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setLocationData(locationStats);

      // Process traffic source data with specific platforms
      const sources = {
        'Google': 0,
        'Facebook': 0,
        'Instagram': 0,
        'Twitter': 0,
        'LinkedIn': 0,
        'YouTube': 0,
        'Direct': 0,
        'Other': 0
      };

      todayVisitors.forEach(v => {
        const referrer = v.referrer || v.source || '';
        const userAgent = v.userAgent || '';
        
        // Detect specific platforms
        if (referrer.includes('google') || referrer.includes('search')) {
          sources.Google++;
        } else if (referrer.includes('facebook') || referrer.includes('fb.com')) {
          sources.Facebook++;
        } else if (referrer.includes('instagram') || referrer.includes('ig.com')) {
          sources.Instagram++;
        } else if (referrer.includes('twitter') || referrer.includes('t.co')) {
          sources.Twitter++;
        } else if (referrer.includes('linkedin')) {
          sources.LinkedIn++;
        } else if (referrer.includes('youtube')) {
          sources.YouTube++;
        } else if (!referrer || referrer === 'Direct') {
          sources.Direct++;
        } else {
          sources.Other++;
        }
      });

      const sourceStats = Object.entries(sources)
        .map(([name, value]) => ({ name, value, icon: getSourceIcon(name) }))
        .filter(s => s.value > 0)
        .sort((a, b) => b.value - a.value);

      setSourceData(sourceStats);

      // Process device data
      const devices = { Desktop: 0, Mobile: 0, Tablet: 0 };
      todayVisitors.forEach(v => {
        if (v.userAgent?.includes('Mobile')) {
          devices.Mobile++;
        } else if (v.userAgent?.includes('Tablet')) {
          devices.Tablet++;
        } else {
          devices.Desktop++;
        }
      });

      const deviceStats = Object.entries(devices)
        .map(([name, value]) => ({ name, value }))
        .filter(d => d.value > 0);

      setDeviceData(deviceStats);

      setVisitors(liveVisitors);
      setLastUpdate(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading visitor data:', error);
      toast.error('Failed to load visitor data');
      setIsLoading(false);
    }
  };

  const COLORS = ['#DC2626', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading visitor dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              <Activity className="h-10 w-10 text-red-600 animate-pulse" />
              Live Visitor Analytics
            </h1>
            <p className="text-gray-700 text-lg font-medium flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" style={{ animationDuration: '3s' }} />
              Real-time monitoring • Updates every 5 seconds
            </p>
          </div>
          <Button 
            onClick={() => navigate('/admin/dashboard')}
            className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Real-time Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card className="border-4 border-green-400 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Live Visitors</p>
                  <p className="text-4xl font-bold text-green-600">{visitorStats.live}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-600 font-medium">Active Now</span>
                  </div>
                </div>
                <Eye className="h-12 w-12 text-green-400 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-4 border-blue-400 shadow-xl bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Today's Visitors</p>
                  <p className="text-4xl font-bold text-blue-600">{visitorStats.today}</p>
                  <p className="text-xs text-gray-500 mt-2">Total unique visits</p>
                </div>
                <Users className="h-12 w-12 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-4 border-purple-400 shadow-xl bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">New Visitors</p>
                  <p className="text-4xl font-bold text-purple-600">{visitorStats.newVisitors}</p>
                  <p className="text-xs text-gray-500 mt-2">First time visits</p>
                </div>
                <TrendingUp className="h-12 w-12 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-4 border-orange-400 shadow-xl bg-gradient-to-br from-orange-50 to-amber-50">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Returning</p>
                  <p className="text-4xl font-bold text-orange-600">{visitorStats.returningVisitors}</p>
                  <p className="text-xs text-gray-500 mt-2">Repeat visits</p>
                </div>
                <Repeat className="h-12 w-12 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live Heartbeat Activity Graph */}
        <Card className="border-4 border-red-400 shadow-2xl bg-white mb-6 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-red-600 to-pink-600 text-white relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12 animate-pulse" style={{ animationDelay: '1s' }}></div>
            <CardTitle className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Activity className="h-8 w-8 animate-pulse text-white" />
                  <div className="absolute -top-1 -right-1">
                    <div className="w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
                    <div className="absolute top-0 w-4 h-4 bg-green-500 rounded-full"></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Live Heartbeat Monitor</h3>
                  <p className="text-sm opacity-90">Real-time visitor pulse</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-bold">LIVE</span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-gradient-to-br from-red-50 to-pink-50">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-red-100 px-3 py-1 rounded-full">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold text-red-700">Heartbeat Active</span>
                </div>
                <p className="text-sm text-gray-600">Updated: {lastUpdate.toLocaleTimeString('en-IN')}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Current BPM (Beats Per Minute)</p>
                <p className="text-3xl font-bold text-red-600 animate-pulse">{visitorStats.live}</p>
              </div>
            </div>
            
            {/* Enhanced Heartbeat Graph */}
            <div className="bg-white p-4 rounded-xl border-4 border-red-200 shadow-inner">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={activityData}>
                  <defs>
                    <linearGradient id="heartbeatGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC2626" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#DC2626" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="pageViewGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EC4899" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#EC4899" stopOpacity={0.1}/>
                    </linearGradient>
                    {/* Heartbeat pulse effect */}
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#fecaca" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#991b1b"
                    style={{ fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <YAxis 
                    stroke="#991b1b"
                    style={{ fontSize: '12px', fontWeight: 'bold' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '3px solid #DC2626',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                    labelStyle={{ fontWeight: 'bold', color: '#DC2626' }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="visitors" 
                    stroke="#DC2626" 
                    strokeWidth={4}
                    fillOpacity={1}
                    fill="url(#heartbeatGradient)" 
                    name="💓 Active Visitors"
                    filter="url(#glow)"
                    animationDuration={1000}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="pageViews" 
                    stroke="#EC4899" 
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#pageViewGradient)" 
                    name="📄 Page Views"
                    animationDuration={1000}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            
            {/* Heartbeat Stats */}
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-4 rounded-lg shadow-lg">
                <p className="text-sm opacity-90">Peak Activity</p>
                <p className="text-3xl font-bold">{Math.max(...activityData.map(d => d.visitors), 0)}</p>
              </div>
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 rounded-lg shadow-lg">
                <p className="text-sm opacity-90">Avg. Visitors</p>
                <p className="text-3xl font-bold">
                  {activityData.length > 0 ? Math.round(activityData.reduce((sum, d) => sum + d.visitors, 0) / activityData.length) : 0}
                </p>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white p-4 rounded-lg shadow-lg">
                <p className="text-sm opacity-90">Total Page Views</p>
                <p className="text-3xl font-bold">{activityData.reduce((sum, d) => sum + d.pageViews, 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Location Distribution */}
          <Card className="border-4 border-blue-400 shadow-xl bg-white lg:col-span-2">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-6 w-6" />
                Visitor Location by City & State
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={locationData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <YAxis 
                    dataKey="location" 
                    type="category" 
                    width={150}
                    stroke="#6b7280"
                    style={{ fontSize: '11px' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '2px solid #3B82F6',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#3B82F6" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Traffic Sources with Social Media */}
          <Card className="border-4 border-purple-400 shadow-xl bg-white">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-6 w-6" />
                Traffic Sources
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={75}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                {sourceData.map((source, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg border-2 border-purple-100 hover:border-purple-300 transition-all">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full shadow-md" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="text-2xl">{source.icon}</span>
                      <span className="text-sm font-semibold">{source.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-purple-600">{source.value}</span>
                      <span className="text-xs text-gray-500 ml-1">visits</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Device Breakdown */}
          <Card className="border-4 border-green-400 shadow-xl bg-white">
            <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-6 w-6" />
                Device Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={deviceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '2px solid #10B981',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Live Visitors Table */}
          <Card className="border-4 border-orange-400 shadow-xl bg-white">
            <CardHeader className="bg-gradient-to-r from-orange-600 to-amber-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-6 w-6" />
                Current Live Visitors
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {visitors.length > 0 ? (
                  visitors.slice(0, 10).map((visitor, index) => (
                    <div key={index} className="bg-gradient-to-r from-gray-50 to-orange-50 p-3 rounded-lg border-2 border-orange-200 hover:border-orange-400 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="font-semibold text-sm">
                            {visitor.location?.city || 'Unknown'}, {visitor.location?.region || 'Unknown'}
                          </span>
                        </div>
                        <Badge className="bg-green-100 text-green-800 text-xs">Live</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div>
                          <span className="font-medium">Page:</span> {visitor.page || '/'}
                        </div>
                        <div>
                          <span className="font-medium">Device:</span> {
                            visitor.userAgent?.includes('Mobile') ? '📱 Mobile' : 
                            visitor.userAgent?.includes('Tablet') ? '📱 Tablet' : '💻 Desktop'
                          }
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p>No live visitors at the moment</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LiveVisitorDashboard;
